import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { TaskType, TaskPriority, TaskStatus, Task } from '../../types/task.types';
import taskService, { CreateTaskDto, UpdateTaskDto } from '../../services/taskService';
import api, { User, Client, Deal, clientsAPI, dealsAPI } from '../../services/api';

interface TaskFormModalProps {
  task?: Task;
  onClose: () => void;
  onSuccess: () => void;
  defaultAssociation?: {
    recordType: string;
    recordId: number;
  };
  defaultClientId?: number;
  defaultDealId?: number;
}

const TaskFormModal: React.FC<TaskFormModalProps> = ({
  task,
  onClose,
  onSuccess,
  defaultAssociation,
  defaultClientId,
  defaultDealId
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  // Client and Deal selection
  const [clients, setClients] = useState<Client[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [dealSearch, setDealSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showDealDropdown, setShowDealDropdown] = useState(false);
  const [selectedClients, setSelectedClients] = useState<Array<Client | { id: number; name: string }>>([]);
  const [selectedDeals, setSelectedDeals] = useState<Array<Deal | { id: number; description: string }>>([]);

  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    type: task?.type || TaskType.TODO,
    priority: task?.priority || TaskPriority.NORMAL,
    status: task?.status || TaskStatus.NOT_STARTED,
    dueDateTime: task?.dueDateTime ? task.dueDateTime.slice(0, 16) : '',
    assignedUserIds: task?.assignments.map((a) => a.userId) || [],
    tags: task?.tags.join(', ') || '',
    associations: defaultAssociation ? [defaultAssociation] : [],
    clientIds: task?.taskClients?.map((tc) => tc.clientId) || (defaultClientId ? [defaultClientId] : []),
    dealIds: task?.taskDeals?.map((td) => td.dealId) || (defaultDealId ? [defaultDealId] : []),
  });

  useEffect(() => {
    loadUsers();
    loadInitialData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearch.trim().length >= 2 || clientSearch.trim().length === 0) {
        searchClients(clientSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  useEffect(() => {
    if (formData.clientIds.length > 0) {
      // Load deals for all selected clients
      formData.clientIds.forEach(clientId => loadDealsForClient(clientId));
    }
  }, [formData.clientIds]);

  const loadInitialData = async () => {
    // Load initial clients if task has them
    if (task?.taskClients && task.taskClients.length > 0) {
      const initialClients = task.taskClients.map(tc => tc.client);
      setSelectedClients(initialClients);
    }
    // Load initial deals if task has them
    if (task?.taskDeals && task.taskDeals.length > 0) {
      const initialDeals = task.taskDeals.map(td => td.deal);
      setSelectedDeals(initialDeals);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    setUsersError(null);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error: any) {
      console.error('Error loading users:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setUsersError('You need admin permissions to assign tasks to users.');
      } else if (error.response?.status === 404) {
        setUsersError('Users endpoint not found. Please contact support.');
      } else {
        setUsersError('Failed to load users. Please try again.');
      }
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const searchClients = async (query: string) => {
    try {
      const data = await clientsAPI.search(query);
      setClients(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error searching clients:', err);
      setClients([]);
    }
  };

  const loadDealsForClient = async (clientId: number) => {
    try {
      const data = await clientsAPI.getDeals(clientId);
      setDeals(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error loading deals:', err);
      setDeals([]);
    }
  };

  const handleClientSelect = (client: Client) => {
    // Add client if not already selected
    if (!selectedClients.find(c => c.id === client.id)) {
      const newClients = [...selectedClients, client];
      setSelectedClients(newClients);
      setFormData(prev => ({ ...prev, clientIds: newClients.map(c => c.id) }));
    }
    setClientSearch('');
    setShowClientDropdown(false);
  };

  const handleClientRemove = (clientId: number) => {
    const newClients = selectedClients.filter(c => c.id !== clientId);
    setSelectedClients(newClients);
    setFormData(prev => ({ ...prev, clientIds: newClients.map(c => c.id) }));
    // Also remove deals associated with this client
    const newDeals = selectedDeals.filter(d => {
      const dealClient = 'client' in d ? d.client : undefined;
      return !dealClient || dealClient.id !== clientId;
    });
    setSelectedDeals(newDeals);
    setFormData(prev => ({ ...prev, dealIds: newDeals.map(d => d.id) }));
  };

  const handleDealSelect = (deal: Deal) => {
    // Add deal if not already selected
    if (!selectedDeals.find(d => d.id === deal.id)) {
      const newDeals = [...selectedDeals, deal];
      setSelectedDeals(newDeals);
      setFormData(prev => ({ ...prev, dealIds: newDeals.map(d => d.id) }));
    }
    setDealSearch('');
    setShowDealDropdown(false);
  };

  const handleDealRemove = (dealId: number) => {
    const newDeals = selectedDeals.filter(d => d.id !== dealId);
    setSelectedDeals(newDeals);
    setFormData(prev => ({ ...prev, dealIds: newDeals.map(d => d.id) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert datetime-local format to ISO-8601
      const convertToISO = (dateTimeLocal: string | undefined): string | undefined => {
        if (!dateTimeLocal) return undefined;
        // datetime-local format: "2025-11-13T20:57"
        // ISO-8601 format: "2025-11-13T20:57:00.000Z"
        return new Date(dateTimeLocal).toISOString();
      };

      if (!task) {
        // Creating new task
        const createData: CreateTaskDto = {
          title: formData.title,
          description: formData.description || undefined,
          type: formData.type,
          priority: formData.priority,
          status: formData.status,
          dueDateTime: convertToISO(formData.dueDateTime),
          tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
          assignedUserIds: formData.assignedUserIds,
          clientIds: formData.clientIds.length > 0 ? formData.clientIds : undefined,
          dealIds: formData.dealIds.length > 0 ? formData.dealIds : undefined,
          associations: formData.associations.length > 0 ? formData.associations.map((a, index) => ({
            ...a,
            isPrimary: index === 0,
          })) : undefined,
        };
        await taskService.createTask(createData);
      } else {
        // Updating existing task
        const updateData: UpdateTaskDto = {
          title: formData.title,
          description: formData.description || undefined,
          type: formData.type,
          priority: formData.priority,
          status: formData.status,
          dueDateTime: convertToISO(formData.dueDateTime),
          tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
          clientIds: formData.clientIds,
          dealIds: formData.dealIds,
        };
        await taskService.updateTask(task.id, updateData);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-primary-600 to-secondary-600 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-2xl font-bold text-white flex items-center">
                      <div className="bg-white/20 rounded-lg p-2 mr-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      {task ? 'Edit Task' : 'Create New Task'}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="text-white/80 hover:text-white transition-colors rounded-lg p-1 hover:bg-white/10"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <p className="mt-2 text-primary-100 text-sm">
                    {task ? 'Update task details and assignments' : 'Create a new task and assign it to team members'}
                  </p>
                </div>

                {/* Form Content */}
                <div className="px-6 py-6">

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Task Title <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                      placeholder="e.g., Follow up with client about proposal"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Description
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                      placeholder="Add more details about this task..."
                    />
                  </div>

                  {/* Client and Deal Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Client Selection */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">
                        Related Clients (Optional)
                      </label>

                      {/* Selected Clients */}
                      {selectedClients.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {selectedClients.map((client) => (
                            <span
                              key={client.id}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {client.name}
                              <button
                                type="button"
                                onClick={() => handleClientRemove(client.id)}
                                className="ml-1 text-primary-600 hover:text-primary-800"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                        placeholder="Search to add clients..."
                      />
                      {showClientDropdown && clients.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-neutral-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {clients.filter(c => !selectedClients.find(sc => sc.id === c.id)).map((client) => (
                            <div
                              key={client.id}
                              onClick={() => handleClientSelect(client)}
                              className="px-4 py-2 hover:bg-primary-50 cursor-pointer text-sm"
                            >
                              <div className="font-medium text-neutral-900">{client.name}</div>
                              {client.email && <div className="text-xs text-neutral-500">{client.email}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Deal Selection */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">
                        Related Deals (Optional)
                      </label>

                      {/* Selected Deals */}
                      {selectedDeals.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {selectedDeals.map((deal) => (
                            <span
                              key={deal.id}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-success-100 text-success-800"
                            >
                              {deal.description}
                              <button
                                type="button"
                                onClick={() => handleDealRemove(deal.id)}
                                className="ml-1 text-success-600 hover:text-success-800"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <input
                        type="text"
                        value={dealSearch}
                        onChange={(e) => {
                          setDealSearch(e.target.value);
                          setShowDealDropdown(true);
                        }}
                        onFocus={() => setShowDealDropdown(true)}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                        placeholder="Search to add deals..."
                      />
                      {showDealDropdown && deals.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-neutral-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {deals.filter(d => !selectedDeals.find(sd => sd.id === d.id)).map((deal) => (
                            <div
                              key={deal.id}
                              onClick={() => handleDealSelect(deal)}
                              className="px-4 py-2 hover:bg-primary-50 cursor-pointer text-sm"
                            >
                              <div className="font-medium text-neutral-900">{deal.description}</div>
                              <div className="text-xs text-neutral-500">${deal.amount} {deal.currency}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Type, Priority, Status Row */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">
                        Type
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => handleChange('type', e.target.value as TaskType)}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                      >
                        {Object.values(TaskType).map((type) => (
                          <option key={type} value={type}>
                            {type.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleChange('priority', e.target.value as TaskPriority)}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                      >
                        {Object.values(TaskPriority).map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleChange('status', e.target.value as TaskStatus)}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                      >
                        {Object.values(TaskStatus).filter(s => s !== TaskStatus.OVERDUE).map((status) => (
                          <option key={status} value={status}>
                            {status.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Due Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.dueDateTime}
                      onChange={(e) => handleChange('dueDateTime', e.target.value)}
                      className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                    />
                  </div>

                  {/* Assigned Users */}
                  {!task && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-3">
                        <UserGroupIcon className="inline-block w-5 h-5 mr-2 -mt-1" />
                        Assign To ({formData.assignedUserIds.length} selected)
                      </label>
                      <div className="max-h-60 overflow-y-auto border border-neutral-300 rounded-md bg-white">
                        {loadingUsers ? (
                          <div className="p-4 text-center text-neutral-500">
                            <svg className="animate-spin h-5 w-5 mx-auto mb-2 text-primary-600" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading users...
                          </div>
                        ) : usersError ? (
                          <div className="p-4 text-center">
                            <div className="text-error-600 text-sm mb-2">⚠️ {usersError}</div>
                            <button
                              type="button"
                              onClick={loadUsers}
                              className="text-xs text-primary-600 hover:text-primary-700 underline"
                            >
                              Try again
                            </button>
                          </div>
                        ) : users.length === 0 ? (
                          <div className="p-4 text-center text-neutral-500">
                            No users available
                          </div>
                        ) : (
                          <div className="divide-y divide-neutral-200">
                            {users.map((user) => {
                              const isSelected = formData.assignedUserIds.includes(user.id);
                              const userName = user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user.email;
                              const userRole = user.role === 'ADMIN' ? 'Admin' : 'User';

                              return (
                                <label
                                  key={user.id}
                                  className={`flex items-center p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
                                    isSelected ? 'bg-primary-50' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        handleChange('assignedUserIds', [...formData.assignedUserIds, user.id]);
                                      } else {
                                        handleChange(
                                          'assignedUserIds',
                                          formData.assignedUserIds.filter((id) => id !== user.id)
                                        );
                                      }
                                    }}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                                  />
                                  <div className="ml-3 flex-1">
                                    <div className="flex items-center justify-between">
                                      <span className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-neutral-900'}`}>
                                        {userName}
                                      </span>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        user.role === 'ADMIN'
                                          ? 'bg-secondary-100 text-secondary-800'
                                          : 'bg-primary-100 text-primary-800'
                                      }`}>
                                        {userRole}
                                      </span>
                                    </div>
                                    <p className="text-xs text-neutral-500 mt-0.5">{user.email}</p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-neutral-500">
                        Select one or more users to assign this task to
                      </p>
                    </div>
                  )}

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleChange('tags', e.target.value)}
                      className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 transition-all px-4 py-2.5 text-sm"
                      placeholder="e.g., urgent, follow-up, important (comma separated)"
                    />
                    <p className="mt-1.5 text-xs text-neutral-500">
                      Separate multiple tags with commas
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-neutral-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2.5 border border-neutral-300 rounded-lg shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : (
                        task ? 'Update Task' : 'Create Task'
                      )}
                    </button>
                  </div>
                </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TaskFormModal;

